/**
 * Paddle.js loader and checkout launcher.
 *
 * The Paddle instance is created lazily and memoised: `initializePaddle` opens
 * a script tag, so calling it per render would be wasteful and can race.
 *
 * We never treat the browser's checkout callback as proof of payment. A
 * successful checkout only tells the UI to start polling; entitlement is
 * granted exclusively by the verified `paddle-webhook` edge function.
 */
import { initializePaddle, type Paddle } from "@paddle/paddle-js";
import {
  PADDLE_CLIENT_TOKEN,
  PADDLE_ENVIRONMENT,
  getPriceId,
  isPaddleConfigured,
  type PlanKey,
} from "@/lib/billingConfig";

let paddlePromise: Promise<Paddle | undefined> | null = null;

export class BillingNotConfiguredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BillingNotConfiguredError";
  }
}

export function getPaddle(): Promise<Paddle | undefined> {
  if (!isPaddleConfigured()) {
    return Promise.reject(
      new BillingNotConfiguredError(
        "Paddle is not configured yet. Add VITE_PADDLE_CLIENT_TOKEN to enable checkout.",
      ),
    );
  }
  paddlePromise ??= initializePaddle({
    environment: PADDLE_ENVIRONMENT,
    token: PADDLE_CLIENT_TOKEN,
  });
  return paddlePromise;
}

export type CheckoutOptions = {
  planKey: PlanKey;
  /** Our user id — echoed back on every webhook so we can attribute the sale. */
  userId: string;
  email?: string | null;
  /** Where Paddle returns the buyer after a successful checkout. */
  successUrl?: string;
};

/**
 * Open the Paddle overlay checkout for a plan.
 *
 * Throws `BillingNotConfiguredError` when the client token or the plan's price
 * ID is missing, so the UI can show a calm "billing not set up yet" state
 * instead of a broken checkout.
 */
export async function openCheckout(opts: CheckoutOptions): Promise<void> {
  const priceId = getPriceId(opts.planKey);
  if (!priceId) {
    throw new BillingNotConfiguredError(
      `No Paddle price ID is configured for the "${opts.planKey}" plan.`,
    );
  }

  const paddle = await getPaddle();
  if (!paddle) {
    throw new BillingNotConfiguredError("Paddle failed to initialize.");
  }

  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    // custom_data on the resulting transaction/subscription. The webhook reads
    // user_id from here to attribute the subscription to an account.
    customData: { user_id: opts.userId },
    customer: opts.email ? { email: opts.email } : undefined,
    settings: {
      displayMode: "overlay",
      theme: document.documentElement.classList.contains("dark") ? "dark" : "light",
      successUrl: opts.successUrl ?? `${window.location.origin}/billing?checkout=complete`,
    },
  });
}
